// ref: docs/CODEMAPS/PATTERNS.md §4.3 Hooks — Extracting UI Logic
import { useEffect, useRef } from 'react';
import { dataURIToUInt8Array } from './handler';
import type { SignatureInputProps } from './types';

// react-signature-canvas ships no types (see src/vendor-modules.d.ts); this covers
// only the methods this hook actually calls on the ref.
interface SignatureCanvasRef {
  toDataURL: () => string;
  clear: () => void;
  fromDataURL: (dataURL: string, options?: { width?: number; height?: number }) => void;
}

/**
 * Wires a signature pad so drawing produces a PNG data URL and erasing clears it. Holds the canvas ref, exposes onEnd which reports the canvas toDataURL output and onClickEraser which clears and reports an empty string, while an effect replays props.signature through fromDataURL at the 552x300 default size or clears the pad when there is none.
 *
 * @param props - The signature input props, carrying the current signature, optional width and height, and the onEndSignature callback.
 * @returns The canvas ref plus the draw-end and erase handlers.
 */
export const useSignatureInput = (props: SignatureInputProps) => {
  const { width = 552, height = 300 } = props;
  const signatureCanvasRef = useRef<SignatureCanvasRef>(null);

  /**
   * Reports the drawn signature as a PNG data URL once a stroke ends. Reads the canvas through toDataURL and passes the result to props.onEndSignature.
   *
   * @returns Nothing.
   */
  const onEnd = () => {
    props.onEndSignature(signatureCanvasRef.current?.toDataURL() ?? '');
  };

  /**
   * Clears the signature pad and reports an empty signature. Calls clear on the canvas ref then invokes props.onEndSignature with an empty string.
   *
   * @returns Nothing.
   */
  const onClickEraser = () => {
    signatureCanvasRef.current?.clear();
    props.onEndSignature('');
  };

  useEffect(() => {
    if (props.signature) {
      // CSP img-src has no `data:`, so a data URI Image src is blocked; use a blob: URL instead.
      const mimeType = props.signature.slice(
        props.signature.indexOf(':') + 1,
        props.signature.indexOf(';'),
      );
      const blob = new Blob([dataURIToUInt8Array(props.signature)], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);
      signatureCanvasRef.current?.fromDataURL(blobUrl, {
        width,
        height,
      });
      return () => URL.revokeObjectURL(blobUrl);
    }
    signatureCanvasRef.current?.clear();
    return undefined;
  }, [props.signature]);

  return { signatureCanvasRef, onEnd, onClickEraser };
};
